import { useEffect, useState, type FormEvent } from "react";

import { ApiError } from "@/api/client";
import {
  getSmtpSettings,
  sendSmtpTest,
  upsertSmtpSettings,
  type SmtpUpsertInput,
} from "@/api/endpoints";
import { InfoTip } from "@/components/InfoTip";
import { formatDateTime } from "@/lib/format";
import { TIPS } from "@/lib/glossary";
import type { SmtpSecurity, SmtpSettings, SmtpTestResult } from "@/types/api";
import { usePageTitle } from "@/hooks/usePageTitle";

const SECURITY: { value: SmtpSecurity; label: string }[] = [
  { value: "starttls", label: "STARTTLS (port 587)" },
  { value: "ssl", label: "Implicit TLS / SSL (port 465)" },
  { value: "none", label: "None — plain (not recommended)" },
];

export function SmtpSettingsAdmin(): JSX.Element {
  usePageTitle("SMTP settings");
  const [current, setCurrent] = useState<SmtpSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [host, setHost] = useState("");
  const [port, setPort] = useState<number>(587);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [clearPassword, setClearPassword] = useState(false);
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [security, setSecurity] = useState<SmtpSecurity>("starttls");

  const [savePending, setSavePending] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveOk, setSaveOk] = useState(false);

  const [testTo, setTestTo] = useState("");
  const [testPending, setTestPending] = useState(false);
  const [testResult, setTestResult] = useState<SmtpTestResult | null>(null);

  useEffect(() => {
    setLoading(true);
    getSmtpSettings()
      .then((s) => {
        setCurrent(s);
        if (s) {
          setHost(s.host);
          setPort(s.port);
          setUsername(s.username ?? "");
          setFromEmail(s.from_email);
          setFromName(s.from_name ?? "");
          setSecurity(s.security);
        }
      })
      .catch((err: unknown) => {
        setLoadError(err instanceof ApiError ? err.message : "Failed to load");
      })
      .finally(() => setLoading(false));
  }, []);

  async function onSave(e: FormEvent): Promise<void> {
    e.preventDefault();
    setSavePending(true);
    setSaveError(null);
    setSaveOk(false);
    if (port < 1 || port > 65535) {
      setSaveError("Enter a port between 1 and 65535.");
      setSavePending(false);
      return;
    }
    const body: SmtpUpsertInput = {
      host,
      port,
      // "" = clear stored password (explicit checkbox only); null = keep.
      password: clearPassword ? "" : passwordTouched ? password : null,
      username: username || null,
      from_email: fromEmail,
      from_name: fromName || null,
      security,
    };
    try {
      const next = await upsertSmtpSettings(body);
      setCurrent(next);
      setPassword("");
      setPasswordTouched(false);
      setClearPassword(false);
      setPasswordTouched(false);
      setSaveOk(true);
    } catch (err) {
      setSaveError(err instanceof ApiError ? err.message : "Save failed");
    } finally {
      setSavePending(false);
    }
  }

  async function onTest(): Promise<void> {
    setTestPending(true);
    setTestResult(null);
    try {
      const r = await sendSmtpTest(testTo);
      setTestResult(r);
    } catch (err) {
      setTestResult({
        ok: false,
        detail: err instanceof ApiError ? err.message : "Test failed",
      });
    } finally {
      setTestPending(false);
    }
  }

  if (loading) return <div className="skeleton h-32" aria-label="Loading" />;

  return (
    <div className="space-y-6">
      <header className="flex items-baseline justify-between">
        <h1 className="text-xl font-semibold">SMTP settings</h1>
        {current && (
          <p className="text-xs text-ink-400">
            Last updated {formatDateTime(current.updated_at)}
          </p>
        )}
      </header>

      {loadError && <p className="text-sm text-severity-high">{loadError}</p>}

      <form onSubmit={(e) => void onSave(e)} className="card space-y-4">
        <h2 className="card-title">Connection</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block space-y-1">
            <span className="text-sm text-ink-200 inline-flex items-center">Host<InfoTip {...TIPS.smtpHost} /></span>
            <input
              type="text"
              required
              className="input"
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="smtp.gmail.com"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-ink-200 inline-flex items-center">Port<InfoTip {...TIPS.smtpPort} /></span>
            <input
              type="number"
              required
              min={1}
              max={65535}
              className="input"
              value={port === 0 ? "" : port}
              onChange={(e) => {
                const n = Number(e.target.value);
                // Empty/garbage must not silently become port 0.
                setPort(e.target.value === "" || !Number.isFinite(n) ? 0 : n);
              }}
            />
          </label>
          <label className="block space-y-1 sm:col-span-2">
            <span className="text-sm text-ink-200 inline-flex items-center">Security<InfoTip {...TIPS.smtpSecurity} /></span>
            <select
              className="input"
              value={security}
              onChange={(e) => setSecurity(e.target.value as SmtpSecurity)}
            >
              {SECURITY.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </label>
        </div>

        <h2 className="card-title">Authentication</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block space-y-1">
            <span className="text-sm text-ink-200 inline-flex items-center">Username<InfoTip {...TIPS.smtpUsername} /></span>
            <input
              type="text"
              autoComplete="off"
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="leave blank for unauthenticated"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-ink-200 inline-flex items-center">
              Password {current?.password_set && <span className="text-ink-400 ml-1">(stored — leave blank to keep)</span>}
              <InfoTip {...TIPS.smtpPassword} />
            </span>
            <input
              type="password"
              autoComplete="new-password"
              className="input"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                // Erasing what you typed reverts to "keep stored password" —
                // previously it silently wiped the stored secret on save.
                setPasswordTouched(e.target.value !== "");
              }}
            />
            {current?.password_set && (
              <label className="flex items-center gap-2 text-xs text-ink-400 mt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={clearPassword}
                  onChange={(e) => setClearPassword(e.target.checked)}
                />
                Remove the stored password (switch to unauthenticated)
              </label>
            )}
          </label>
        </div>

        <h2 className="card-title">Sender identity</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block space-y-1">
            <span className="text-sm text-ink-200 inline-flex items-center">From email<InfoTip {...TIPS.smtpFromEmail} /></span>
            <input
              type="email"
              required
              className="input"
              value={fromEmail}
              onChange={(e) => setFromEmail(e.target.value)}
              placeholder="noreply@yourdomain.com"
            />
          </label>
          <label className="block space-y-1">
            <span className="text-sm text-ink-200 inline-flex items-center">From name (optional)<InfoTip {...TIPS.smtpFromName} /></span>
            <input
              type="text"
              className="input"
              value={fromName}
              onChange={(e) => setFromName(e.target.value)}
              placeholder="AquaTrack"
            />
          </label>
        </div>

        {saveError && <p className="text-sm text-severity-high">{saveError}</p>}
        {saveOk && <p className="text-sm text-severity-ok">Saved.</p>}

        <button type="submit" className="btn-primary" disabled={savePending}>
          {savePending ? "Saving…" : "Save settings"}
        </button>
      </form>

      {current && (
        <section className="card space-y-3">
          <h2 className="card-title">Send test email</h2>
          <p className="text-sm text-ink-300">
            Sends a one-line test message using the saved settings to verify
            connectivity, authentication, and DNS.
          </p>
          <div className="flex gap-2 items-end">
            <label className="block space-y-1 flex-1">
              <span className="text-sm text-ink-200">Recipient</span>
              <input
                type="email"
                className="input"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
                placeholder="you@yourdomain.com"
              />
            </label>
            <button
              type="button"
              className="btn-primary"
              disabled={!testTo || testPending}
              onClick={() => void onTest()}
            >
              {testPending ? "Sending…" : "Send test"}
            </button>
          </div>
          {testResult && (
            testResult.ok ? (
              <p className="text-sm text-severity-ok">Test message sent.</p>
            ) : (
              <p className="text-sm text-severity-high">
                Failed: <code className="text-xs">{testResult.detail}</code>
              </p>
            )
          )}
        </section>
      )}
    </div>
  );
}
